import React, { useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import styled from "styled-components";
import tinyMCEImageUpload from "services/firebase/tinyMCEImageUpload";
import { ErrorBoundary } from "@sentry/react";
import ErrorFallback from "features/errorCatching/components/ErrorFallback";

interface Props {
    handleEditorData: (rawData: string, id: string) => void;
    value: string;
    id: string;
}

const TextEditor: React.FC<Props> = ({
    handleEditorData,
    value,
    id = "uuid",
}) => {
    const [key, setKey] = useState<number>(0);
    const editorId = id + JSON.stringify(key);

    return (
        <Container>
            <ErrorBoundary
                fallback={ErrorFallback}
                onReset={() => setKey(key + 1)}
            >
                <Editor
                    apiKey={process.env.REACT_APP_TINY_API_KEY}
                    cloudChannel="5-stable"
                    disabled={false}
                    id={editorId}
                    init={{
                        images_upload_handler: tinyMCEImageUpload,
                        resize: true,
                        height: 300,
                        content_style: "body, td, pre {font-size: 20px;}",
                        plugins:
                            "print preview paste importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons",
                        menubar:
                            "file edit view insert format tools table help",
                        toolbar:
                            "undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen preview print | insertfile image media link anchor codesample | ltr rtl",
                        toolbar_sticky: true,
                        autosave_ask_before_unload: true,
                        autosave_interval: "30s",
                        autosave_prefix: "{path}{query}-{id}-",
                        autosave_restore_when_empty: false,
                        autosave_retention: "2m",
                        image_advtab: true,
                        importcss_append: true,
                        image_caption: true,
                        quickbars_selection_toolbar:
                            "bold italic | quicklink h2 h3 blockquote quickimage quicktable",
                        noneditable_noneditable_class: "mceNonEditable",
                        toolbar_mode: "sliding",
                        contextmenu: "link image imagetools table",
                    }}
                    inline={false}
                    onEditorChange={(a) => handleEditorData(a, editorId)}
                    tagName="div"
                    textareaName=""
                    value={value}
                    key={key}
                />
            </ErrorBoundary>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-flow: column;

    & .tox-mbtn {
        font-size: 0.9rem !important;
        margin-right: 1rem !important;
    }
`;

export default React.memo((props: Props) => <TextEditor {...props} />);
